import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  signIn,
  signUp,
  confirmSignUp,
  signOut,
  getToken,
  getCurrentUser,
} from './auth';
import {
  createSession,
  startSession,
  getSession,
  listSessions,
  deleteSession,
  updateSession,
} from './api';
import './App.css';

function useTheme() {
  const [theme, setTheme] = useState(() => {
    return document.documentElement.getAttribute('data-theme') || 'dark';
  });

  useEffect(() => {
    // Habilitar transiciones después del primer pintado
    requestAnimationFrame(() => {
      document.documentElement.classList.add('theme-transitions-enabled');
    });
  }, []);

  const toggleTheme = useCallback(() => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    setTheme(next);
    try {
      localStorage.setItem('hermes-theme', next);
    } catch (e) {}
  }, [theme]);

  return { theme, toggleTheme };
}

function ThemeToggle({ theme, onToggle }) {
  return (
    <button
      onClick={onToggle}
      aria-label={
        theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'
      }
      className="theme-toggle"
      type="button"
    >
      {theme === 'dark' ? '☀️' : '🌙'}
    </button>
  );
}

function App() {
  const { theme, toggleTheme } = useTheme();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [view, setView] = useState('upload'); // upload | sessions | detail
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    client: '',
    meeting: '',
  });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const u = getCurrentUser();
    if (u) {
      setUser(u);
      getToken()
        .then(setToken)
        .catch(() => setUser(null));
    }
  }, []);

  const handleLogout = () => {
    signOut();
    setUser(null);
    setToken(null);
  };

  if (!user)
    return (
      <AuthForm
        onAuth={(u, t) => {
          setUser(u);
          setToken(t);
        }}
      />
    );

  return (
    <div className="app">
      <header>
        <h1>⚡ Hermes</h1>
        <nav>
          <button
            className={view === 'upload' ? 'active' : ''}
            onClick={() => setView('upload')}
          >
            Nueva Sesión
          </button>
          <button
            className={view === 'sessions' ? 'active' : ''}
            onClick={() => {
              setView('sessions');
              loadSessions();
            }}
          >
            Historial
          </button>
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
          <button className="logout" onClick={handleLogout}>
            Salir
          </button>
        </nav>
      </header>

      {message && (
        <div className="message">
          {message}
          <button className="message-close" onClick={() => setMessage('')}>✕</button>
        </div>
      )}

      {view === 'upload' && (
        <UploadView
          token={token}
          setMessage={setMessage}
          onCreated={(s) => {
            setCurrentSession(s);
            setView('detail');
          }}
        />
      )}
      {view === 'sessions' && (
        <div className="sessions-layout">
          <FilterPanel
            filters={filters}
            onChange={(f) => {
              setFilters(f);
              setPage(1);
              loadSessions(f, 1);
            }}
          />
          <div className="sessions-main">
            <SessionList
              sessions={sessions}
              token={token}
              onSelect={(s) => {
                setCurrentSession(s);
                setView('detail');
                loadDetail(s.session_id);
              }}
              onDelete={handleDelete}
              onEdit={handleEdit}
            />
            <Pagination
              page={page}
              total={total}
              pageSize={10}
              onPageChange={(p) => {
                setPage(p);
                loadSessions(filters, p);
              }}
            />
          </div>
        </div>
      )}
      {view === 'detail' && currentSession && (
        <DetailView
          session={currentSession}
          summary={summary}
          token={token}
          onRefresh={() => loadDetail(currentSession.session_id)}
        />
      )}
    </div>
  );

  async function handleDelete(sessionId) {
    if (
      !window.confirm(
        '¿Eliminar esta sesión? Esta acción no se puede deshacer.',
      )
    )
      return;
    await deleteSession(sessionId, token);
    loadSessions(filters, page);
  }

  async function handleEdit(sessionId, data) {
    await updateSession(sessionId, data, token);
    loadSessions(filters, page);
  }

  async function loadSessions(f, p) {
    const activeFilters = f || filters;
    const activePage = p || page;
    const data = await listSessions(token, {
      status: 'COMPLETED',
      ...activeFilters,
      page: activePage,
      page_size: 10,
    });
    setSessions(data.sessions || []);
    setTotal(data.total || 0);
  }

  async function loadDetail(id) {
    const data = await getSession(id, token);
    setCurrentSession(data);
    if (data.summary_url) {
      const res = await fetch(data.summary_url);
      setSummary(await res.text());
    } else {
      setSummary('');
    }
  }
}

function AuthForm({ onAuth }) {
  const [mode, setMode] = useState('login'); // login | register | confirm
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const { theme, toggleTheme } = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (mode === 'register') {
        await signUp(email, password);
        setMode('confirm');
      } else if (mode === 'confirm') {
        await confirmSignUp(email, code);
        setMode('login');
      } else {
        const token = await signIn(email, password);
        onAuth(getCurrentUser(), token);
      }
    } catch (err) {
      setError(err.message || 'Error');
    }
  };

  return (
    <div className="auth">
      <ThemeToggle theme={theme} onToggle={toggleTheme} />
      <h1>⚡ Hermes</h1>
      <p className="subtitle">Asistente de resúmenes de reuniones</p>
      <form onSubmit={handleSubmit}>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        {mode !== 'confirm' && (
          <input
            type="password"
            placeholder="Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        )}
        {mode === 'confirm' && (
          <input
            type="text"
            placeholder="Código de verificación"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        )}
        {error && <p className="error">{error}</p>}
        <button type="submit">
          {mode === 'login'
            ? 'Iniciar Sesión'
            : mode === 'register'
              ? 'Registrarse'
              : 'Verificar'}
        </button>
      </form>
      {mode === 'login' && (
        <p className="toggle">Contacta al administrador para obtener acceso</p>
      )}
      {mode === 'confirm' && (
        <p className="toggle" onClick={() => setMode('login')}>
          Volver al login
        </p>
      )}
    </div>
  );
}

function UploadView({ token, setMessage, onCreated }) {
  const [file, setFile] = useState(null);
  const [type, setType] = useState('work');
  const [client, setClient] = useState('');
  const [meeting, setMeeting] = useState('');
  const [agenda, setAgenda] = useState('');
  const [participants, setParticipants] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');

  const handleDrop = (e) => {
    e.preventDefault();
    setFile(e.dataTransfer.files[0]);
  };
  const handleDragOver = (e) => e.preventDefault();

  const generateFilename = () => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const aa = String(now.getFullYear()).slice(-2);
    const c = client.trim() || 'Sin-Cliente';
    const m = meeting.trim() || 'Sin-Reunion';
    return `${dd}${mm}${aa}_${c}_${m}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);

    try {
      setProgress('Creando sesión...');
      const filename = generateFilename();
      const session = await createSession(
        {
          filename,
          context: { type, client, meeting, agenda, participants, language: 'es' },
        },
        token,
      );

      setProgress('Subiendo video...');
      await fetch(session.upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'application/octet-stream' },
      });

      setProgress('Iniciando procesamiento...');
      await startSession(session.session_id, token);

      setProgress('');
      setMessage(
        `Sesión ${session.session_id} en procesamiento. Recibirás una notificación al completar.`,
      );
      onCreated(session);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <form className="upload" onSubmit={handleSubmit}>
      <div
        className="dropzone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => document.getElementById('fileInput').click()}
      >
        {file
          ? `📁 ${file.name} (${(file.size / 1024 / 1024).toFixed(0)} MB)`
          : '📂 Arrastra un video aquí o haz clic para seleccionar'}
        <input
          id="fileInput"
          type="file"
          accept=".mp4,.webm,.mkv,.flac,.wav,.m4a"
          onChange={(e) => setFile(e.target.files[0])}
          hidden
        />
      </div>
      <h3>Contexto (opcional)</h3>
      <select value={type} onChange={(e) => setType(e.target.value)}>
        <option value="work">📋 Reunión de trabajo</option>
        <option value="training">🎓 Capacitación / Training</option>
      </select>
      <input
        placeholder="Cliente"
        value={client}
        onChange={(e) => setClient(e.target.value)}
      />
      <input
        placeholder="Reunión"
        value={meeting}
        onChange={(e) => setMeeting(e.target.value)}
      />
      <input
        placeholder="Agenda"
        value={agenda}
        onChange={(e) => setAgenda(e.target.value)}
      />
      <input
        placeholder="Participantes"
        value={participants}
        onChange={(e) => setParticipants(e.target.value)}
      />
      <button type="submit" disabled={!file || uploading}>
        {uploading ? progress : '🚀 Procesar Reunión'}
      </button>
    </form>
  );
}

function FilterPanel({ filters, onChange }) {
  const setDateRange = (from, to) =>
    onChange({ ...filters, date_from: from, date_to: to });

  const today = () => {
    const d = new Date().toISOString().slice(0, 10);
    setDateRange(d, d);
  };
  const thisWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const mon = new Date(now);
    mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
    setDateRange(
      mon.toISOString().slice(0, 10),
      now.toISOString().slice(0, 10),
    );
  };
  const thisMonth = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    setDateRange(
      first.toISOString().slice(0, 10),
      now.toISOString().slice(0, 10),
    );
  };

  return (
    <div className="filter-panel">
      <h3>🔍 Filtros</h3>
      <label>Fecha desde</label>
      <input
        type="date"
        value={filters.date_from}
        onChange={(e) => onChange({ ...filters, date_from: e.target.value })}
      />
      <label>Fecha hasta</label>
      <input
        type="date"
        value={filters.date_to}
        onChange={(e) => onChange({ ...filters, date_to: e.target.value })}
      />
      <div className="date-shortcuts">
        <button onClick={today}>Hoy</button>
        <button onClick={thisWeek}>Esta semana</button>
        <button onClick={thisMonth}>Este mes</button>
      </div>
      <label>Cliente</label>
      <input
        type="text"
        placeholder="Buscar cliente..."
        value={filters.client}
        onChange={(e) => onChange({ ...filters, client: e.target.value })}
      />
      <label>Reunión</label>
      <input
        type="text"
        placeholder="Buscar reunión..."
        value={filters.meeting}
        onChange={(e) => onChange({ ...filters, meeting: e.target.value })}
      />
      {(filters.date_from ||
        filters.date_to ||
        filters.client ||
        filters.meeting) && (
        <button
          className="clear-filters"
          onClick={() =>
            onChange({ date_from: '', date_to: '', client: '', meeting: '' })
          }
        >
          ✕ Limpiar filtros
        </button>
      )}
    </div>
  );
}

function SessionList({ sessions, token, onSelect, onDelete, onEdit }) {
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  if (!sessions.length)
    return <p className="empty">No se encontraron sesiones.</p>;

  const startEdit = (s, e) => {
    e.stopPropagation();
    setEditId(s.session_id);
    setEditData({
      client: s.context?.client || '',
      meeting: s.context?.meeting || '',
    });
  };

  const cancelEdit = (e) => {
    e.stopPropagation();
    setEditId(null);
  };

  const saveEdit = (sessionId, e) => {
    e.stopPropagation();
    onEdit(sessionId, { client: editData.client, meeting: editData.meeting });
    setEditId(null);
  };

  return (
    <div className="sessions">
      <div className="session-header">
        <span>Fecha</span>
        <span>Cliente</span>
        <span>Reunión</span>
        <span>Estado</span>
        <span></span>
      </div>
      {sessions.map((s) => (
        <div
          key={s.session_id}
          className="session-row"
          onClick={() => editId !== s.session_id && onSelect(s)}
        >
          {editId === s.session_id ? (
            <>
              <span className="session-date">
                {new Date(s.created_at).toLocaleDateString()}
              </span>
              <input
                className="edit-input"
                value={editData.client}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setEditData({ ...editData, client: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && saveEdit(s.session_id, e)}
              />
              <input
                className="edit-input"
                value={editData.meeting}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setEditData({ ...editData, meeting: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && saveEdit(s.session_id, e)}
              />
              <span className={`status-badge ${s.status}`}>{s.status}</span>
              <span className="row-actions">
                <button className="save-btn" onClick={(e) => saveEdit(s.session_id, e)} title="Guardar">✓</button>
                <button className="cancel-btn" onClick={cancelEdit} title="Cancelar">✕</button>
              </span>
            </>
          ) : (
            <>
              <span className="session-date">
                {new Date(s.created_at).toLocaleDateString()}
              </span>
              <span className="session-client">{s.context?.client || '—'}</span>
              <span className="session-meeting">
                {s.context?.meeting || s.original_filename || s.session_id}
              </span>
              <span className={`status-badge ${s.status}`}>{s.status}</span>
              <span className="row-actions">
                <button className="edit-btn" onClick={(e) => startEdit(s, e)} title="Editar">✏️</button>
                <button className="delete-btn" onClick={(e) => { e.stopPropagation(); onDelete(s.session_id); }} title="Eliminar">✕</button>
              </span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function Pagination({ page, total, pageSize, onPageChange }) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
        ← Anterior
      </button>
      <span>
        {page} / {totalPages}
      </span>
      <button
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        Siguiente →
      </button>
    </div>
  );
}

function DetailView({ session, summary, token, onRefresh }) {
  const downloadMd = () => {
    const blob = new Blob([summary], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${session.original_filename || session.session_id}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadTranscript = async () => {
    if (!session.transcript_url) return;
    const res = await fetch(session.transcript_url);
    const data = await res.json();
    const text =
      data.results?.transcripts?.[0]?.transcript ||
      JSON.stringify(data, null, 2);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${session.original_filename || session.session_id}-transcripcion.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="detail">
      <div className="detail-header">
        <h2>{session.original_filename || session.session_id}</h2>
        <span className={`status-badge ${session.status}`}>
          {session.status}
        </span>
        {session.status !== 'COMPLETED' && (
          <button onClick={onRefresh}>🔄 Actualizar</button>
        )}
        {session.status === 'COMPLETED' && (
          <button onClick={onRefresh}>🔄 Recargar</button>
        )}
        {summary && <button onClick={downloadMd}>📥 Resumen .md</button>}
        {session.transcript_url && (
          <button onClick={downloadTranscript}>📝 Transcripción</button>
        )}
      </div>
      {summary ? (
        <div className="markdown-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {summary.replace(/!\[\[(.+?)\]\]/g, '`📎 Imagen: $1`')}
          </ReactMarkdown>
        </div>
      ) : (
        <p className="processing">
          ⏳ Procesando... actualiza en unos minutos.
        </p>
      )}
    </div>
  );
}

export default App;
